import { useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';
import { useI18n } from '@bravemove/i18n/react/core';
import { useOverlayFocus } from '../../utils/useOverlayFocus';
import { useEntranceMotion } from '../../utils/useEntranceMotion';

export type DrawerSide = 'start' | 'end';

export interface DrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly side?: DrawerSide;
  readonly title?: ReactNode;
  readonly children?: ReactNode;
  readonly footer?: ReactNode;
  readonly width?: number | string;
  readonly showCloseButton?: boolean;
  readonly dismissOnBackdrop?: boolean;
  readonly dismissOnEscape?: boolean;
  readonly className?: string;
  readonly ariaLabel?: string;
  readonly closeLabel?: string;
}

export function Drawer({
  open,
  onClose,
  side = 'end',
  title,
  children,
  footer,
  width = 360,
  showCloseButton = true,
  dismissOnBackdrop = true,
  dismissOnEscape = true,
  className,
  ariaLabel,
  closeLabel,
}: DrawerProps) {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useOverlayFocus(open, ref, onClose, dismissOnEscape);
  useEntranceMotion(ref, open);

  if (!open || typeof document === 'undefined') return null;

  const sideClass = side === 'end' ? 'inset-y-0 end-0 border-s' : 'inset-y-0 start-0 border-e';

  return createPortal(
    <div
      role="presentation"
      onClick={(e) => {
        if (dismissOnBackdrop && e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
    >
      <aside
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={
          !title ? (ariaLabel ?? t('common:dialog.title', { defaultValue: 'Dialog' })) : undefined
        }
        tabIndex={-1}
        style={{ width, maxWidth: '100%' }}
        className={cn(
          'absolute h-full bg-surface text-ink',
          'shadow-[0_16px_40px_-20px_rgba(0,0,0,0.20)]',
          'flex flex-col overflow-hidden border-hairline focus:outline-none',
          sideClass,
          className,
        )}
      >
        {title || showCloseButton ? (
          <header className="flex items-center justify-between gap-3 p-4 border-b border-hairline">
            {title ? (
              <h2 id={titleId} className="text-[15px] font-bold text-ink truncate">
                {title}
              </h2>
            ) : (
              <span />
            )}
            {showCloseButton ? (
              <button
                type="button"
                aria-label={closeLabel ?? t('common:actions.close', { defaultValue: 'Close' })}
                onClick={onClose}
                className="size-10 shrink-0 inline-flex items-center justify-center rounded-md text-ink-2 hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text"
              >
                <CloseIcon size={18} color="currentColor" />
              </button>
            ) : null}
          </header>
        ) : null}
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
        {footer ? (
          <footer className="p-3 border-t border-hairline flex flex-wrap items-center justify-end gap-2 bg-surface-2/40">
            {footer}
          </footer>
        ) : null}
      </aside>
    </div>,
    document.body,
  );
}
